/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './ImageCropper';
import { helpRenders } from './ImageCropper_Help';

const ImageCropperManager: ComponentManagerFn<'ImageCropper'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ImageCropper'>(platform, 'ImageCropper'),
    type: 'ImageCropper',
    name: 'Image Cropper',
    isChildAllowed: () => false,
    defaultValue,
    props: {
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Textarea'],
        preventMultiEdit: true,
        helpText:
          'Choose an attribute that contains the value for the src attribute.',
      },
    },
    getDisplayText: (item) => item.props.viewAttribute,
    category: 'Forms',
    icon: { iconName: 'image' },
    description: `**Overview**<br/>
The Image Cropper component provides an interface for users to upload, view, and edit images directly within the application. It specifically enables cropping functionality, allowing users to adjust the framing or aspect ratio of an image before saving it.
Use this component for profile picture uploads, product image management, or any scenario where user-uploaded visual content needs to be standardized or trimmed.

**1. Data Binding**<br/>
The component handles the image data by binding to a text-based field in your database.
* **View Attribute:** Selects the Data Store field that holds the image data.
* **Supported Type:** **Textarea**.
* **Storage Mechanism:** This attribute typically stores the source of the cropped image (often as a Base64 encoded string or a URL path) so it can be rendered later.

**2. Functionality**<br/>
Unlike the standard **Image** or **Media** components which are primarily for display, the Image Cropper is an active input tool.
* **Upload:** Allows users to select a local file from their device.
* **Crop:** Provides a UI overlay with draggable corners to select a specific region of the image.
* **Render:** Displays the final processed image based on the bound view attribute.

**3. Best Use Cases**<br/>
* **User Profiles:** Allowing users to upload a photo and crop it to a perfect circle or square for their avatar.
* **Content Management:** Ensuring article thumbnails meet specific aspect ratio requirements (e.g., 16:9) by forcing a crop during upload.
* **Identity Verification:** Trimming scanned ID documents to focus on the ID card and remove background noise.`,
    helpRenders,
  };
};

export default ImageCropperManager;
